/**
 * Created by neo on 08.02.17.
 */
import { MediaSize, createMediaSize } from './MediaSize';
import { HttpBackend } from '../../Services/Http/HttpBackend';
import { MediaOverlay, createMediaOverlay, getOverlayStartSeconds } from './MediaOverlay';
import { MediaTextTrack, createMediaTextTrack } from './MediaTextTrack';
import { MediaTranscriptCue } from './MediaTranscriptCue';
import { MediaVideoTrack, createMediaVideoTrack } from './MediaVideoTrack';
import { MediaInfo } from './MediaInfo';
import { MediaLoop, createMediaLoop, getLoopStartSeconds } from './MediaLoop';
import { Audited, createAuditedFields } from '../Audited';
import { MediaVisibleRect, createMediaVisibleRect } from './MediaVisibleRect';
import { MediaVideoAssets, createMediaVideoAssets } from './MediaVideoAssets';
import { createMediaAudioTrack, MediaAudioTrack } from './MediaAudioTrack';

export function isMediaImage(media: Media): boolean {
  return media.mediaType.startsWith('image');
}

export function isMediaVideo(media: Media): boolean {
  return media.mediaType.startsWith('video');
}

export function isMediaMp4(media: Media): boolean {
  return media.mediaType?.toLowerCase().startsWith('video/');
}

export function getMediaSmallest(media: Media): string {
  return (
    media.sizes.find((s) => s.size === 'small')?.url ??
    media.sizes.find((s) => s.size === 'medium')?.url ??
    media.sizes.find((s) => s.size === 'large')?.url ??
    media.url
  );
}

export function getMediaSmallestOrNothing(media: Media): string | undefined {
  return media.videoAssets?.hls ?? media.sizes.find((s) => s.size === 'small')?.url;
}

export function getMediaMedium(media: Media): string {
  return media.videoAssets?.hls ?? media.sizes.find((s) => s.size === 'medium')?.url ?? getMediaSmallest(media);
}

export function getMediaMediumOrSmallest(media: Media): string {
  return media.videoAssets?.hls ?? media.sizes.find((s) => s.size === 'medium')?.url ?? getMediaSmallest(media);
}

export function getMediaLarge(media: Media): string {
  return media.videoAssets?.hls ?? media.sizes.find((s) => s.size === 'large')?.url ?? media.url;
}

export function getMediaLargeOrMedium(media: Media): string {
  return media.videoAssets?.hls ?? media.sizes.find((s) => s.size === 'large')?.url ?? getMediaMedium(media);
}

export function getMediaLargeOrMediumOrSmallest(media: Media): string {
  return media.videoAssets?.hls ?? media.sizes.find((s) => s.size === 'large')?.url ?? getMediaMediumOrSmallest(media);
}

export function getMediaVideoUri(media: Media): string | undefined {
  if (!isMediaVideo(media)) return undefined;
  return (
    media.videoAssets?.hls ??
    media.sizes.find((s) => s.size === 'medium')?.url ??
    media.sizes.find((s) => s.size === 'small')?.url ??
    media.sizes.find((s) => s.size === 'large')?.url ??
    (isMediaMp4(media) ? media.url : undefined)
  );
}

export function getMediaVideoUriObject(media: Media): { uri: string } | undefined {
  const videoUri = getMediaVideoUri(media);
  return videoUri ? { uri: videoUri } : undefined;
}

export function getMediaUriObject(media: Media): { uri: string } {
  return { uri: media.url ?? '' };
}

export function getMediaSmallestUriObject(media: Media): { uri: string } {
  return { uri: getMediaSmallest(media) };
}

export function getMediaMediumOrSmallestUriObject(media: Media): { uri: string } {
  return { uri: getMediaMediumOrSmallest(media) };
}

export function getMediaLargeOrMediumOrSmallestUriObject(media: Media): { uri: string } {
  return { uri: getMediaLargeOrMediumOrSmallest(media) };
}

export type Media = Audited & {
  language?: string;
  mediaType: string;
  name?: string;
  description?: string;
  url: string;
  size?: number;
  sizes: MediaSize[];
  mediaInfo: MediaInfo;
  overlay: MediaOverlay[];
  loops: MediaLoop[];
  audioTracks: MediaAudioTrack[];
  textTracks: MediaTextTrack[];
  videoTracks: MediaVideoTrack[];
  transcript?: MediaTranscriptCue[];
  visibleRect?: MediaVisibleRect;
  apiVideoId?: string;
  videoAssets?: MediaVideoAssets;
  aiContent: boolean;
};

/** @deprecated Use raw object instead */
export function createMedia(json?: Partial<Media>): Media {
  return {
    ...createAuditedFields(json),
    language: json?.language,
    mediaType: json?.mediaType || 'unknown',
    name: json?.name,
    description: json?.description,
    url: json?.url || '',
    size: json?.size,
    sizes: (json?.sizes || []).map((s) => createMediaSize(s)),
    mediaInfo: json?.mediaInfo ?? {},
    overlay: (json?.overlay ?? [])
      .map((o) => createMediaOverlay(o))
      .sort((a, b) => getOverlayStartSeconds(a) - getOverlayStartSeconds(b)),
    loops: (json?.loops ?? [])
      .map((o) => createMediaLoop(o))
      .sort((a, b) => getLoopStartSeconds(a) - getLoopStartSeconds(b)),
    audioTracks: (json?.audioTracks ?? []).map((t) => createMediaAudioTrack(t)),
    textTracks: (json?.textTracks ?? []).map((t) => createMediaTextTrack(t)),
    videoTracks: (json?.videoTracks ?? []).map((t) => createMediaVideoTrack(t)),
    transcript: json?.transcript ? json.transcript.map((c) => ({ ...c })) : undefined,
    visibleRect: json?.visibleRect ? createMediaVisibleRect(json.visibleRect) : undefined,
    apiVideoId: json?.apiVideoId,
    videoAssets: json?.videoAssets ? createMediaVideoAssets(json.videoAssets) : undefined,
    aiContent: json?.aiContent ?? false,
  };
}

// Static API methods
export const MediaApi = {
  get(mediaId: string): Promise<Media | undefined> {
    return HttpBackend.get(`/media/media/findOne/${mediaId}`).then((media) => (media ? createMedia(media) : undefined));
  },

  list(uid: string): Promise<Media[]> {
    return HttpBackend.get(`/media/${uid}/medias`).then((res) => (res ?? []).map((m) => createMedia(m)));
  },
};
